import * as PIXI from 'pixi.js';
import BytesToChunksTransformer from './apngDecoder/transformers/bytesToChunksTransformer';
import FrameToPngTransformer, { RawPngFrame } from './apngDecoder/transformers/frameToPngTransformer';
import StreamableAnimatedSprite, { PngFrameTexture } from './streamableAnimatedSprite';
import { getSearchParams } from './utils';

import type { LoaderParser } from 'pixi.js';

const animatedPngLoader = {
    name: 'loadApng',
    extension: {
        type: PIXI.ExtensionType.LoadParser,
        priority: PIXI.LoaderParserPriority.Low,
    },
    test(url, asset)
    {
        return asset?.format === 'apng'
            || PIXI.checkDataUrl(url, 'image/apng')
            || PIXI.checkExtension(url, '.apng')
            || getSearchParams(url).get('format') === 'apng';
    },
    async load(url, asset)
    {
        const response = await PIXI.settings.ADAPTER.fetch(url);
        const separateFramesTransformer = new FrameToPngTransformer();

        if (!response.body)
        {
            throw new Error('Unable to fetch request body');
        }

        const cachePrefix = asset?.alias?.[0] ?? url;

        const pngFramesStream = response.body
            .pipeThrough(new TransformStream(new BytesToChunksTransformer()))
            .pipeThrough(new TransformStream(separateFramesTransformer))
            .pipeThrough(new TransformStream<RawPngFrame, PngFrameTexture>({
                async transform({ metadata, dataChunks }, controller)
                {
                    const imageBitmap = await createImageBitmap(new Blob(dataChunks));

                    (imageBitmap as any)._pixiId = `${cachePrefix}_frame_${metadata.frameNumber}`;
                    const texture = PIXI.Texture.from(imageBitmap);

                    controller.enqueue({
                        metadata,
                        texture
                    });
                }
            }));

        return new StreamableAnimatedSprite(
            pngFramesStream,
            () =>
            {
                const animationInfo = separateFramesTransformer.getAnimationInfo();

                if (!animationInfo)
                {
                    throw new Error('Unable to read animation info');
                }

                return {
                    ...animationInfo,
                    name: asset?.alias?.[0] ?? url,
                };
            });
    },
    async unload(asset)
    {
        await asset.abortLoading('Unloading resource');
        asset.destroy(true);
    },
} as LoaderParser<StreamableAnimatedSprite>;

PIXI.extensions.add(animatedPngLoader);
